/**
 * registry/replacements.ts — сборка ЧИСТЫХ modify*, replace*-хуков из реестра и
 * их применение к урону/смерти (R3/D16, tech/22 §3.1, tech/23 §5).
 *
 * Ядро вызывает эти хуки СИНХРОННО ДО применения операции (не через очередь §5),
 * на урезанном PureContext (без ops/rng/choose — чистота типом, S9). Отсюда
 * инвертированная зависимость: реализация живёт здесь (нужен реестр), а ops
 * получает её как OpsDeps (bindOps(draft, queue, deps)).
 *
 * Порядок применения — КАНОНИЧЕСКИЙ (tech/23 §5): эффекты по scope
 * table→lane→cell→player→unit, затем числовой seq. Для outgoing intrinsic hook
 * источника образует первый слой перед covering effects; в остальных chains
 * собственный хук юнита-носителя идёт как scope unit. Обычный damage проходит
 * outgoing → incoming → replaceDamage → random plan → interception → Shield.
 * Combat groups применяют outgoing/replacement/random один раз, incoming — на part.
 */
import { buildPureContext, buildSynchronousInterceptorContext } from '../ctx/context.js';
import { isUnitOnFieldById } from '../effects/gc.js';
import { rngChance } from '../rng/rng.js';
import { canonicalCellOrder } from '../state/board.js';
import { assertFiniteNumber, normalizeZero } from '../state/num.js';
import { coveringEffects, SCOPE_RANK } from '../stats/effectiveStats.js';
const SOURCE_KIND_EFFECT = 0;
const SOURCE_KIND_SELF = 1;
/** Канонический порядок источников хуков: scope-уровень → вид → числовой seq. */
function compareSources(a, b) {
    if (a.scopeRank !== b.scopeRank)
        return a.scopeRank - b.scopeRank;
    if (a.sourceKind !== b.sourceKind)
        return a.sourceKind - b.sourceKind;
    return a.seq - b.seq;
}
/** Хуки определения по источнику (юнит-носитель или эффект). */
function hooksOf(registry, source) {
    return registry.get(source.defId)?.hooks;
}
/**
 * Источники хуков со стороны ЮНИТА: покрывающие его эффекты (по scope-цепочке) +
 * собственный хук юнита (scope unit). Оба участвуют в модификаторах/замене урона
 * и в replaceDeath (tech/23 §5, tech/22 §3.1).
 */
function unitHookSources(draft, instanceId) {
    const inst = draft.instances[instanceId];
    if (inst === undefined)
        return [];
    const sources = [];
    for (const effect of coveringEffects(draft, instanceId)) {
        sources.push({
            subject: { effectId: effect.id },
            defId: effect.defId,
            scopeRank: SCOPE_RANK[effect.scope.kind],
            sourceKind: SOURCE_KIND_EFFECT,
            seq: effect.seq,
        });
    }
    if (isUnitOnFieldById(draft, instanceId)) {
        sources.push({
            subject: { selfId: instanceId },
            defId: inst.defId,
            scopeRank: SCOPE_RANK.unit,
            sourceKind: SOURCE_KIND_SELF,
            seq: inst.seq,
        });
    }
    return sources.sort(compareSources);
}
/**
 * Outgoing damage has one explicit layer boundary: the unit's intrinsic hook
 * runs before environmental effects covering it. Effects retain the shared
 * table→lane→cell→player→unit/seq order. This lets a specific cell bonus add
 * after the source's own arithmetic without any card-specific priority.
 */
function outgoingHookSources(draft, instanceId) {
    const sources = unitHookSources(draft, instanceId);
    return [
        ...sources.filter((source) => source.sourceKind === SOURCE_KIND_SELF),
        ...sources.filter((source) => source.sourceKind === SOURCE_KIND_EFFECT),
    ];
}
/** Источники хуков со стороны ИГРОКА-цели (эффекты scope player этого игрока). */
function playerHookSources(draft, playerId) {
    const sources = [];
    for (const effect of draft.effects) {
        if (effect.scope.kind === 'player' && effect.scope.playerId === playerId) {
            sources.push({
                subject: { effectId: effect.id },
                defId: effect.defId,
                scopeRank: SCOPE_RANK.player,
                sourceKind: SOURCE_KIND_EFFECT,
                seq: effect.seq,
            });
        }
    }
    return sources.sort(compareSources);
}
/** Источники со стороны ЦЕЛИ (юнит → его цепочка; игрок → player-эффекты). */
function targetHookSources(draft, planned) {
    return planned.target.kind === 'unit'
        ? unitHookSources(draft, planned.target.instanceId)
        : playerHookSources(draft, planned.target.playerId);
}
/** Применить цепочку числовых модификаторов (outgoing/incoming) → новая величина. */
function applyModifiers(draft, registry, sources, which, planned) {
    let amount = planned.amount;
    for (const source of sources) {
        const fn = hooksOf(registry, source)?.[which];
        if (typeof fn !== 'function')
            continue;
        const ctx = buildPureContext(draft, source.subject, registry);
        const next = fn(ctx, { ...planned, amount });
        assertFiniteNumber(next, `хук ${which} карты "${source.defId}"`);
        amount = next;
    }
    return normalizeZero(amount);
}
/** Outgoing combat phase over one aggregate attack-group amount. */
export function applyOutgoingDamageModifiers(registry, draft, planned) {
    if (planned.sourceId === undefined)
        return planned;
    const amount = applyModifiers(draft, registry, outgoingHookSources(draft, planned.sourceId), 'modifyOutgoingDamage', planned);
    return { ...planned, amount };
}
/** Incoming pure modifiers over one already-split attack part. */
export function applyIncomingDamageModifiers(registry, draft, planned) {
    const amount = applyModifiers(draft, registry, targetHookSources(draft, planned), 'modifyIncomingDamage', planned);
    return { ...planned, amount };
}
/** Применить цепочку replaceDamage (редирект/отмена) → инструкция либо null. */
function applyReplaceDamage(draft, registry, sources, planned) {
    let current = planned;
    for (const source of sources) {
        const fn = hooksOf(registry, source)?.replaceDamage;
        if (typeof fn !== 'function')
            continue;
        const ctx = buildPureContext(draft, source.subject, registry);
        const res = fn(ctx, current);
        if (res === null)
            return null; // урон предотвращён
        validateReplacementInstruction(draft, res, source.defId, current.origin, 'replaceDamage');
        current = res; // смена target/targetCellId = редирект
    }
    return current;
}
/** Group-level target replacement/cancellation before interception and Reaction. */
export function applyDamageReplacement(registry, draft, planned) {
    return applyReplaceDamage(draft, registry, targetHookSources(draft, planned), planned);
}
/** Run the attacker's intrinsic preparation before every ordinary/extra group. */
export function applyOwnAttackPreparation(registry, draft, ops, planned) {
    const attacker = draft.instances[planned.attackerId];
    if (attacker?.cellId === undefined || !isUnitOnFieldById(draft, attacker.id))
        return;
    const hook = registry.get(attacker.defId)?.hooks?.beforeOwnAttack;
    if (hook === undefined)
        return;
    hook(buildSynchronousInterceptorContext(draft, ops, { selfId: attacker.id }, registry), planned);
}
/** Run every intrinsic allied support on the board, canonically. */
export function applyAlliedAttackSupporters(registry, draft, ops, planned) {
    const attacker = draft.instances[planned.attackerId];
    if (attacker?.cellId === undefined || !isUnitOnFieldById(draft, attacker.id))
        return;
    for (const cellId of canonicalCellOrder) {
        const occupantId = draft.board.cells[cellId]?.occupantId;
        if (occupantId === null || occupantId === undefined || occupantId === attacker.id)
            continue;
        const supporter = draft.instances[occupantId];
        if (supporter?.cellId === undefined
            || supporter.controllerId !== attacker.controllerId
            || !isUnitOnFieldById(draft, supporter.id))
            continue;
        const hook = registry.get(supporter.defId)?.hooks?.beforeAlliedAttack;
        if (hook === undefined)
            continue;
        hook(buildSynchronousInterceptorContext(draft, ops, { selfId: supporter.id }, registry), planned);
    }
}
/**
 * Run target-side seeded interceptors in canonical scope order. State writes are
 * applied immediately through Ops, so a later group observes the serialized
 * once-state. The first successful interceptor cancels the remaining checks.
 */
export function applyAttackInterceptors(registry, draft, ops, planned) {
    const checks = [];
    for (const source of targetHookSources(draft, planned)) {
        const hook = hooksOf(registry, source)?.interceptAttack;
        if (hook === undefined)
            continue;
        const ctx = buildSynchronousInterceptorContext(draft, ops, source.subject, registry);
        const result = hook(ctx, planned);
        if (result === undefined || result === null)
            continue;
        if (typeof result.abilityId !== 'string'
            || result.abilityId === ''
            || typeof result.avoided !== 'boolean') {
            throw new Error(`interceptAttack карты "${source.defId}" вернул некорректный результат`);
        }
        const sourceId = ctx.self?.id ?? ctx.effect?.sourceId ?? ctx.attachment?.sourceCardInstanceId;
        checks.push({
            ...(sourceId !== undefined ? { sourceId } : {}),
            abilityId: result.abilityId,
            succeeded: result.avoided,
        });
        if (result.avoided)
            return { avoided: true, checks };
    }
    return { avoided: false, checks };
}
/** Stateful per-part target mitigation immediately before the Shield snapshot. */
export function applyIncomingDamageInterceptors(registry, draft, ops, planned) {
    let amount = planned.amount;
    const transfers = [];
    for (const source of targetHookSources(draft, planned)) {
        const hook = hooksOf(registry, source)?.interceptIncomingDamage;
        if (hook === undefined)
            continue;
        const ctx = buildSynchronousInterceptorContext(draft, ops, source.subject, registry);
        const next = hook(ctx, { ...planned, amount });
        if (next === undefined || next === null)
            continue;
        const where = `interceptIncomingDamage карты "${source.defId}"`;
        if (typeof next === 'number') {
            assertFiniteNumber(next, where);
            amount = next;
            continue;
        }
        assertFiniteNumber(next.amount, `${where}: amount`);
        amount = next.amount;
        if (next.transfer === undefined)
            continue;
        const transfer = next.transfer;
        if (typeof transfer.instanceId !== 'string' || transfer.instanceId === '') {
            throw new Error(`${where}: transfer.instanceId должен быть непустой строкой`);
        }
        if (!Number.isSafeInteger(transfer.amount) || transfer.amount < 0) {
            throw new Error(`${where}: transfer.amount должен быть неотрицательным safe integer`);
        }
        if (!isUnitOnFieldById(draft, transfer.instanceId)) {
            throw new Error(`${where}: transfer.instanceId "${transfer.instanceId}" не существует на поле`);
        }
        if (transfer.amount === 0)
            continue;
        const sourceId = ctx.self?.id ?? ctx.effect?.sourceId ?? ctx.attachment?.sourceCardInstanceId;
        transfers.push({
            instanceId: transfer.instanceId,
            amount: transfer.amount,
            ...(sourceId !== undefined ? { sourceId } : {}),
        });
    }
    return { instruction: { ...planned, amount: normalizeZero(amount) }, transfers };
}
function validateReplacementInstruction(draft, instruction, defId, expectedOrigin, hookName) {
    const where = `${hookName} карты "${defId}"`;
    assertFiniteNumber(instruction.base, `${where}: base`);
    assertFiniteNumber(instruction.amount, `${where}: amount`);
    if (!['ordinary_attack', 'extra_attack', 'effect'].includes(instruction.origin)) {
        throw new Error(`${where}: неизвестный origin "${String(instruction.origin)}"`);
    }
    if (instruction.origin !== expectedOrigin) {
        throw new Error(`${where}: origin является стабильной атрибуцией и не может быть изменён`);
    }
    if (instruction.sourceId !== undefined &&
        (typeof instruction.sourceId !== 'string' || instruction.sourceId === '')) {
        throw new Error(`${where}: sourceId должен быть непустой строкой`);
    }
    const target = instruction.target;
    if (target?.kind === 'unit') {
        if (typeof target.instanceId !== 'string' || target.instanceId === '') {
            throw new Error(`${where}: unit target.instanceId должен быть непустой строкой`);
        }
        const instance = draft.instances[target.instanceId];
        if (instance === undefined || !isUnitOnFieldById(draft, target.instanceId)) {
            throw new Error(`${where}: unit target "${target.instanceId}" не существует на поле`);
        }
        if (instruction.targetCellId !== undefined && instruction.targetCellId !== instance.cellId) {
            throw new Error(`${where}: targetCellId "${instruction.targetCellId}" не совпадает с ячейкой unit "${String(instance.cellId)}"`);
        }
    }
    else if (target?.kind === 'player') {
        if (typeof target.playerId !== 'string' || draft.players[target.playerId] === undefined) {
            throw new Error(`${where}: player target "${String(target.playerId)}" не существует`);
        }
        if (instruction.targetCellId !== undefined) {
            const cell = draft.board.cells[instruction.targetCellId];
            if (cell === undefined) {
                throw new Error(`${where}: targetCellId "${instruction.targetCellId}" не существует`);
            }
            if (cell.side !== draft.players[target.playerId]?.side) {
                throw new Error(`${where}: targetCellId относится к другой стороне, чем player target`);
            }
        }
    }
    else {
        throw new Error(`${where}: неизвестный target.kind "${String(target?.kind)}"`);
    }
}
/**
 * Полная цепочка обычной (не group-split) инструкции: outgoing атакующего →
 * incoming цели → replaceDamage. clamp делает ops.dealDamage. null ⇒ предотвращён.
 */
export function applyDamageReplacements(registry, draft, planned) {
    const afterOutgoing = applyOutgoingDamageModifiers(registry, draft, planned);
    const afterIncoming = applyIncomingDamageModifiers(registry, draft, afterOutgoing);
    return applyDamageReplacement(registry, draft, afterIncoming);
}
function publicInstruction(instruction) {
    return {
        target: structuredClone(instruction.target),
        ...(instruction.targetCellId !== undefined ? { targetCellId: instruction.targetCellId } : {}),
        amount: instruction.amount,
    };
}
/**
 * Executes at most one canonical pure random-replacement plan. The hook only
 * describes probability/outcome; this function advances the authoritative RNG.
 */
export function applyRandomDamageReplacement(registry, draft, planned) {
    for (const source of targetHookSources(draft, planned)) {
        const planner = hooksOf(registry, source)?.planRandomDamageReplacement;
        if (planner === undefined)
            continue;
        const plan = planner(buildPureContext(draft, source.subject, registry), planned);
        if (plan === null)
            continue;
        if (typeof plan.id !== 'string' || plan.id === '') {
            throw new Error(`planRandomDamageReplacement карты "${source.defId}": id должен быть непустой строкой`);
        }
        assertFiniteNumber(plan.probability, `planRandomDamageReplacement карты "${source.defId}": probability`);
        if (plan.probability < 0 || plan.probability > 1) {
            throw new Error(`planRandomDamageReplacement карты "${source.defId}": probability должна быть в диапазоне 0..1`);
        }
        if (plan.replacement !== null) {
            validateReplacementInstruction(draft, plan.replacement, source.defId, planned.origin, 'planRandomDamageReplacement');
        }
        const [succeeded, nextRng] = rngChance(draft.rng, plan.probability);
        draft.rng = nextRng;
        const instruction = succeeded ? plan.replacement : planned;
        return {
            instruction,
            event: {
                type: 'RandomDamageReplacementResolved',
                id: plan.id,
                probability: plan.probability,
                succeeded,
                ...(planned.sourceId !== undefined ? { sourceId: planned.sourceId } : {}),
                origin: planned.origin,
                original: publicInstruction(planned),
                result: instruction === null ? null : publicInstruction(instruction),
            },
        };
    }
    return { instruction: planned };
}
/**
 * replaceDeath (D16): первый релевантный хук в каноническом порядке решает исход
 * (юнит ещё на поле — destroy это проверяет). null ⇒ замены нет, Гибель продолжается.
 */
export function applyDeathReplacement(registry, draft, instanceId) {
    for (const source of unitHookSources(draft, instanceId)) {
        const fn = hooksOf(registry, source)?.replaceDeath;
        if (typeof fn !== 'function')
            continue;
        const ctx = buildPureContext(draft, source.subject, registry);
        return fn(ctx);
    }
    return null;
}
/**
 * Собрать OpsDeps из реестра: подключает чистые replacement-хуки к dealDamage/
 * destroy-швам ops (bindOps(draft, queue, deps)). Так модификаторы/замена
 * работают на ЛЮБОМ dealDamage/destroy (полный боевой снимок — M4).
 */
export function makeReplacementDeps(registry) {
    return {
        isCoolingPrevented: (defId) => registry.get(defId)?.keywords?.includes('CANNOT_COOL') === true,
        tokenBaseStats: (defId) => {
            const definition = registry.get(defId);
            return definition === undefined ? undefined : definition.baseStats ?? { attack: 0, health: 1 };
        },
        applyDamageReplacements: (draft, planned) => applyDamageReplacements(registry, draft, planned),
        applyRandomDamageReplacement: (draft, planned) => applyRandomDamageReplacement(registry, draft, planned),
        applyIncomingDamageInterceptors: (draft, ops, planned) => applyIncomingDamageInterceptors(registry, draft, ops, planned),
        applyDeathReplacement: (draft, instanceId) => applyDeathReplacement(registry, draft, instanceId),
    };
}
//# sourceMappingURL=replacements.js.map